// Verify Email landing page -- iteration 076
// Public page that handles email verification token links.

import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

type VerifyState = "loading" | "success" | "error" | "expired";

export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const purpose = searchParams.get("purpose") ?? "";

  const [state, setState] = useState<VerifyState>("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token || !purpose) {
      setState("error");
      setMessage("Invalid verification link.");
      return;
    }

    fetch(`/api/verify-email/${token}?purpose=${encodeURIComponent(purpose)}`)
      .then(async (res) => {
        if (res.ok) {
          setState("success");
          setMessage("Your email has been verified successfully!");
        } else {
          const body = await res.json().catch(() => ({ error: "" }));
          const errorMsg = body.error ?? "";
          if (errorMsg.includes("expired") || errorMsg.includes("already used")) {
            setState("expired");
            setMessage("This verification link has expired or has already been used.");
          } else {
            setState("error");
            setMessage(errorMsg || "Invalid verification link.");
          }
        }
      })
      .catch(() => {
        setState("error");
        setMessage("Something went wrong. Please try again later.");
      });
  }, [token, purpose]);

  return (
    <div
      data-page="verify-email"
      className="flex min-h-screen items-center justify-center bg-muted/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-background p-8 shadow-sm border">
        {state === "loading" && (
          <div className="text-center">
            <div className="mb-4 text-lg font-medium text-muted-foreground">Verifying...</div>
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        )}

        {state === "success" && (
          <div className="text-center">
            <div className="mb-2 text-4xl">&#10003;</div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">Verified!</h1>
            <p className="mb-6 text-sm text-muted-foreground">{message}</p>
            <button
              data-action="go-to-dashboard"
              onClick={() => navigate("/")}
              className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {state === "expired" && (
          <div className="text-center">
            <div className="mb-2 text-4xl">&#9200;</div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">Link Expired</h1>
            <p className="mb-6 text-sm text-muted-foreground">{message}</p>
            <button
              data-action="go-to-login"
              onClick={() => navigate("/login")}
              className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Go to Login
            </button>
          </div>
        )}

        {state === "error" && (
          <div className="text-center">
            <div className="mb-2 text-4xl">&#10060;</div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">Verification Failed</h1>
            <p className="mb-6 text-sm text-muted-foreground">{message}</p>
            <button
              data-action="go-to-login"
              onClick={() => navigate("/login")}
              className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
